import React, { useState, useEffect, useRef } from 'react';
import microphoneIcon from '../../img/agent/microphone.svg';
import trashIcon from '../../img/agent/trash.svg';
import historyIcon from '../../img/agent/history.svg';
import plusIcon from '../../img/agent/plus.svg';
import maleIcon from '../../img/agent/male.svg';
import femaleIcon from '../../img/agent/female.svg';
import fileIcon from '../../img/agent/file.svg';
import chatIcon from '../../img/agent/chat.svg';
import { modeOptions } from './constants';

// ════════════════════════════════════════════════════════
//  Переиспользуемые суб-компоненты для AI-Pirogov-dev
// ════════════════════════════════════════════════════════

// ── Побуквенное появление текста ──
// animate=false → текст сразу (для исторических сообщений)
export const TypewriterText = ({ text, duration = 1500, animate = true }) => {
  const fullText = String(text || '');
  const [visibleLen, setVisibleLen] = useState(animate ? 0 : fullText.length);

  useEffect(() => {
    if (!animate) { setVisibleLen(fullText.length); return; }
    if (!fullText.length) return;
    setVisibleLen(0);
    const interval = Math.max(duration / fullText.length, 1);
    let i = 0;
    const timer = setInterval(() => {
      i++;
      setVisibleLen(i);
      if (i >= fullText.length) clearInterval(timer);
    }, interval);
    return () => clearInterval(timer);
  }, [fullText, duration, animate]);

  if (!animate || visibleLen >= fullText.length) return <span>{fullText}</span>;

  return (
    <span>
      <span>{fullText.slice(0, visibleLen)}</span>
      <span style={{ opacity: 0 }}>{fullText.slice(visibleLen)}</span>
    </span>
  );
};

// ── Плеер с визуализацией волны (Telegram-style) ──
export const WaveformPlayer = ({ src, className }) => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [bars, setBars] = useState(() =>
    Array.from({ length: 40 }, () => 0.12 + Math.random() * 0.88)
  );
  const rafRef = useRef(null);

  useEffect(() => {
    if (!src) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(src);
        const buf = await res.arrayBuffer();
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const decoded = await ctx.decodeAudioData(buf);
        if (cancelled) return;
        const raw = decoded.getChannelData(0);
        const count = 40;
        const block = Math.floor(raw.length / count);
        const heights = [];
        for (let i = 0; i < count; i++) {
          let sum = 0;
          for (let j = 0; j < block; j++) sum += Math.abs(raw[i * block + j]);
          heights.push(sum / block);
        }
        const max = Math.max(...heights, 0.01);
        setBars(heights.map(h => Math.max(h / max, 0.08)));
      } catch { /* keep random bars */ }
    })();
    return () => { cancelled = true; };
  }, [src]);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    const tick = () => {
      if (a.duration) { setProgress(a.currentTime / a.duration); setDuration(a.duration); }
      if (!a.paused) rafRef.current = requestAnimationFrame(tick);
    };
    const onPlay  = () => { setPlaying(true);  rafRef.current = requestAnimationFrame(tick); };
    const onPause = () => { setPlaying(false); cancelAnimationFrame(rafRef.current); };
    const onEnded = () => { setPlaying(false); setProgress(0); cancelAnimationFrame(rafRef.current); };
    const onMeta  = () => setDuration(a.duration);
    a.addEventListener('play', onPlay);
    a.addEventListener('pause', onPause);
    a.addEventListener('ended', onEnded);
    a.addEventListener('loadedmetadata', onMeta);
    return () => {
      a.removeEventListener('play', onPlay);
      a.removeEventListener('pause', onPause);
      a.removeEventListener('ended', onEnded);
      a.removeEventListener('loadedmetadata', onMeta);
      cancelAnimationFrame(rafRef.current);
    };
  }, [src]);

  const toggle = () => { const a = audioRef.current; if (a) a.paused ? a.play() : a.pause(); };
  const seek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const frac = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    const a = audioRef.current;
    if (a && a.duration) { a.currentTime = frac * a.duration; setProgress(frac); }
  };
  const fmt = (s) => {
    if (!s || !isFinite(s)) return '0:00';
    return `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
  };
  const filled = Math.floor(progress * bars.length);

  return (
    <div className={`pd-waveform${className ? ' ' + className : ''}`}>
      <audio ref={audioRef} src={src} preload="metadata" />
      <button type="button" className="pd-waveform__play" onClick={toggle}
        aria-label={playing ? 'Pause' : 'Play'}>
        {playing ? (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
            <rect x="6" y="4" width="4" height="16" rx="1"/>
            <rect x="14" y="4" width="4" height="16" rx="1"/>
          </svg>
        ) : (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
            <polygon points="7,4 21,12 7,20"/>
          </svg>
        )}
      </button>
      <div className="pd-waveform__bars" onClick={seek}>
        {bars.map((h, i) => (
          <div key={i} className={`pd-waveform__bar${i < filled ? ' pd-waveform__bar--filled' : ''}`}
               style={{ height: `${h * 100}%` }}/>
        ))}
      </div>
      <span className="pd-waveform__time">{playing ? fmt(audioRef.current?.currentTime) : fmt(duration)}</span>
    </div>
  );
};

// ── Пузырь пользователя ──
export const UserBubble = ({ children }) => (
  <div className="pd-msg-right">
    <div className="pd-user-bubble">{children}</div>
  </div>
);

// ── Пузырь ИИ (текст / TypewriterText) ──
export const AiBubble = ({ text, animate = false, variant = 'left' }) => (
  <div className={variant === 'pop' ? 'pd-msg-pop' : 'pd-msg-left'}>
    <div className="pd-ai-bubble">
      {animate ? <TypewriterText text={text} /> : text}
    </div>
  </div>
);

// ── Текст с сохранением переносов строк (без анимации) ──
export const MultiLineText = ({ text }) =>
  String(text || '').split('\n').map((line, i, arr) => (
    <React.Fragment key={i}>{line}{i < arr.length - 1 && <br />}</React.Fragment>
  ));

// ── Блок «Мысли ИИ» ──
export const AiThought = ({ text, animate = false, teal = false }) => (
  <div className={`pd-ai-thought${teal ? ' pd-ai-thought--teal' : ''}`}>
    <span className="pd-ai-thought__label">🧠 Мысли ИИ:</span>
    {animate
      ? <TypewriterText text={text} />
      : <MultiLineText text={text} />
    }
  </div>
);

// ── Кнопка «Скачать сцену» ──
export const DownloadBtn = ({ onClick, initial = false }) => (
  <button
    onClick={onClick}
    className={`pd-download-btn${initial ? ' pd-download-btn--initial' : ''}`}
  >Скачать сцену</button>
);

// ── Превью аудиозаписи ──
export const AudioPreview = ({ src, onDelete, disabled = false }) => (
  <div className="pd-audio-preview">
    <WaveformPlayer src={src} />
    <button
      type="button" onClick={onDelete} disabled={disabled}
      className="pd-audio-preview__delete" title="Удалить запись"
    ><img src={trashIcon} alt="trash" /></button>
  </div>
);

// ── Унифицированная форма ввода (обновление / уточнение) ──
export const ChatInputForm = ({
  value, onChange, onSubmit, placeholder,
  disabled, isRecording, recordingRef,
  onStartRecording, onStopRecording,
  audioBlob, audioUrl, onDeleteRecording,
  submitDisabled, submitTitle, autoFocus,
}) => (
  <form onSubmit={onSubmit}>
    {!audioBlob && (
      <div className="pd-input-wrap">
        <textarea
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          rows={3}
          autoFocus={autoFocus}
          disabled={disabled}
          className="pd-chat-textarea"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onSubmit(e); }
            if (e.key === ' ' && !value && !audioBlob && !disabled) { e.preventDefault(); onStartRecording(e); }
          }}
          onKeyUp={(e) => {
            if (e.key === ' ' && recordingRef.current) { onStopRecording(e); }
          }}
        />
        <div className="pd-input-buttons">
          <button
            type="button" disabled={disabled}
            onMouseDown={onStartRecording} onMouseUp={onStopRecording}
            onMouseLeave={onStopRecording} onTouchStart={onStartRecording}
            onTouchEnd={onStopRecording} onTouchCancel={onStopRecording}
            onContextMenu={(e) => e.preventDefault()}
            className={`pd-mic-btn${isRecording ? ' pd-mic-btn--recording' : ''}`}
            title="Удерживайте для записи"
          >
            <img src={microphoneIcon} alt="mic"
              className={`pd-mic-icon${isRecording ? ' pd-mic-icon--recording' : ''}`} />
          </button>
          <button
            type="submit" disabled={submitDisabled}
            className="pd-send-btn" title={submitTitle}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff"
              strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="19" x2="12" y2="5" />
              <polyline points="5 12 12 5 19 12" />
            </svg>
          </button>
        </div>
      </div>
    )}
    {audioBlob && (
      <div className="pd-audio-preview">
        <WaveformPlayer src={audioUrl} />
        <div className="pd-audio-preview__actions">
          <button
            type="button" onClick={onDeleteRecording} disabled={disabled}
            className="pd-audio-preview__delete" title="Удалить запись"
          ><img src={trashIcon} alt="trash" /></button>
          <button
            type="submit" disabled={submitDisabled}
            className="pd-send-btn" title={submitTitle}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff"
              strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="19" x2="12" y2="5" />
              <polyline points="5 12 12 5 19 12" />
            </svg>
          </button>
        </div>
      </div>
    )}
  </form>
);

// ── Модалка «Как пользоваться» ──
export const InstructionsModal = ({ selectedMode, onSelectMode, currentInstruction, onClose }) => (
  <div className="pd-overlay" onClick={onClose}>
    <div className="pd-modal pd-modal--instructions" onClick={(e) => e.stopPropagation()}>
      <div className="pd-modal__header">
        <span className="pd-modal__title">📚 Как пользоваться</span>
        <button type="button" onClick={onClose} className="pd-modal__close">✕</button>
      </div>
      <div className="pd-modal__body">
        <div className="pd-mode-tabs">
          {modeOptions.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => onSelectMode(opt.value)}
              className={`pd-mode-tab${selectedMode === opt.value ? ' pd-mode-tab--active' : ''}`}
            >{opt.label}</button>
          ))}
        </div>
        <div className="pd-instruction-title">{currentInstruction.title}</div>
        <div className="pd-instruction-desc">{currentInstruction.description}</div>
        {currentInstruction.examples.map((ex, idx) => (
          <div key={idx} className="pd-instruction-example">
            <div className="pd-instruction-formula">{ex.formula}</div>
            <ul className="pd-instruction-items">
              {ex.items.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          </div>
        ))}
        <div className="pd-instruction-tip">
          💡 <strong>Цветовые акценты:</strong> {currentInstruction.colorInfo}
        </div>
      </div>
    </div>
  </div>
);

// ── Диалог подтверждения смены тела ──
export const BodySwitchModal = ({ selectedBody, onClose }) => (
  <div className="pd-overlay" onClick={onClose}>
    <div className="pd-modal pd-modal--confirm" onClick={(e) => e.stopPropagation()}>
      <div className="pd-confirm-title">Сменить тело?</div>
      <div className="pd-confirm-desc">
        Для работы с другим телом нужно начать новый чат. Текущий чат будет закрыт.
      </div>
      <div className="pd-confirm-actions">
        <button onClick={onClose} className="pd-btn pd-header-btn">Отмена</button>
        <button
          onClick={() => {
            const newBody = 'structures';
            const params = new URLSearchParams(window.location.search);
            params.set('body', newBody);
            window.location.href = `${window.location.pathname}?${params}`;
          }}
          className="pd-header-btn"
        ><img src={plusIcon} alt="" className="pd-btn-icon" /> <span>Новый чат</span></button>
      </div>
    </div>
  </div>
);

// ── Приветственное сообщение ──
export const WelcomeMessage = () => (
  <div className="pd-welcome">
    <div className="pd-welcome__bubble">
      <div className="pd-welcome__greeting">Привет! 👋</div>
      <div>Я — Brain AI, ваш помощник для создания анатомических 3D-сцен.</div>
      <div>Вот что я умею:</div>
      <ul className="pd-welcome__list">
        <li>Работать в режимах: <b>обычный</b>, <b>клиницист</b> и <b>хирург</b></li>
        <li>Создавать анатомические сцены по текстовому или голосовому описанию</li>
        <li>Редактировать уже готовую сцену — добавлять, удалять, менять цвета структур</li>
      </ul>
      <div className="pd-welcome__hint">Опишите, что хотите увидеть, и я создам сцену для вас.</div>
    </div>
  </div>
);
